import { useMemo } from 'react';
import { useTheme } from 'foundations-theming/utils';
import { FiltersDisplay } from 'customer-data-filters-ui/public/components';
import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { Link } from 'foundations-components/transitional/components/Link';
import { getTableLabelWithSelfJoins } from '../../../../utils/table-utils';
import { getDangerousRollupPropertyTableLabel, isDangerousRollupProperty, shouldHideProperty } from '../../../../utils/source-utils';
import { useProperty, useTableDescription, useTableSource, useTableSources
// @ts-expect-error untyped import
} from '../SourceMeta';
import { useSnowflakeJoinMetadata } from '../../../../metadata/joins-with-meta';
import { useSnowflakeObjectStrategy } from '../../strategies/use-snowflake-object-strategy';
import { getFilterFamily, getReportingFilterContext, transformToFilterBranch } from '../../../../utils/filter-utils';
import { DataSourceTypes } from '../../../../schema/data-source';
import { isRelativeTimeProperty } from '../../../../utils/column-utils';
// @ts-expect-error untyped import
import { shouldUseLegacyDateTimeFilter } from '../../xo-filter-utils';
import { useFilterRelevantUserInfo } from '../../../../../shared/lib/utils';

// TODO: remove this when we add more structured metadata validation
// @returns {{ error?: React.ReactNode, warning?: React.ReactNode }}
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const validateProperty = (property, tableDescription, tables) => {
  if (shouldHideProperty(property)) {
    return {
      error: I18n.text('reporting-snowflake.metadata-errors.invalid-property')
    };
  }
  if (isDangerousRollupProperty(tableDescription, property)) {
    return {
      warning: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "reporting-snowflake.metadata-warnings.dangerous-rollup-property_jsx",
        elements: {
          Link
        },
        options: {
          external: true,
          tableName: getDangerousRollupPropertyTableLabel(tableDescription, tables, property),
          href: 'https://knowledge.hubspot.com/reports/understand-the-custom-report-builder#rollup-fields'
        }
      })
    };
  }
  return {};
};
const PropertyFilterDisplayLabel = ({
  filter
}) => {
  const trellisTheme = useTheme();
  const {
    field,
    filter: propertyFilter
  } = filter;
  const {
    table: tableName,
    name: fieldName
  } = field;
  const tables = useTableSources();
  const source = useTableSource(tableName);
  const property = useProperty(tableName, fieldName);
  const tableDescription = useTableDescription();
  const {
    data: allJoinMetadata
  } = useSnowflakeJoinMetadata();
  const {
    userGates
  } = useFilterRelevantUserInfo();
  const reportingFilterContext = getReportingFilterContext(source, property);
  const isRtp = isRelativeTimeProperty(property);
  const shouldUseLegacyDateFilter = shouldUseLegacyDateTimeFilter(propertyFilter, userGates);
  const strategy = useSnowflakeObjectStrategy({
    source,
    reportingFilterContext,
    isRelativeTimeProperty: isRtp,
    shouldUseLegacyDateFilter,
    userGates
  });
  if (property == null) {
    throw new Error(`PropertyFilterDisplayLabel can only be rendered when there is a matching existing property in report meta! field: ${JSON.stringify(field)}`);
  }
  if (tableDescription == null || source == null) {
    throw new Error(`PropertyFilterDisplayLabel can only be rendered when there is a table description in the report! field: ${JSON.stringify(field)}`);
  }
  const tableLabel = getTableLabelWithSelfJoins(tableName, tableDescription, source, allJoinMetadata);
  const filtersDisplayValue = useMemo(() => {
    return transformToFilterBranch(filter, property.name);
  }, [filter, property]);
  const isVirtualDataSource = source.dataSourceType === DataSourceTypes.VIRTUAL_DATASOURCE;

  // The property naming convention for virtual data sources is:
  // <object type family>/<object type name>/<property name>, for eg, AD_CAMPAIGN/hs_ad_account_id
  // We need to use the object type family as the filter family for virtual data sources
  const baseFilterFamily = isVirtualDataSource && property.name.includes('/') ? property.name.split('/')[0] : getFilterFamily(source);
  const {
    error,
    warning
  } = validateProperty(property, tableDescription, tables);
  const style = error ? {
    color: trellisTheme.color['text-alert-default']
  } : {};
  const tooltipMessage = error || warning;
  if (!filtersDisplayValue) {
    return null;
  }
  if (!strategy) {
    return null;
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "property-filter-display-label",
    children: [/*#__PURE__*/_jsx("span", {
      style: {
        display: 'block',
        fontWeight: 600,
        marginBottom: -4
      },
      children: /*#__PURE__*/_jsx(TruncateString, {
        children: tableLabel
      })
    }), /*#__PURE__*/_jsx(Tooltip, {
      title: tooltipMessage,
      disabled: !tooltipMessage,
      children: /*#__PURE__*/_jsx("span", {
        style: style,
        children: /*#__PURE__*/_jsx(FiltersDisplay, {
          baseFilterFamily: baseFilterFamily,
          strategy: strategy,
          value: filtersDisplayValue
        })
      })
    })]
  });
};
export default PropertyFilterDisplayLabel;